import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '../../../../../../shared/components';
import {
  BuildingConstruction,
  LandUseChange,
  RoadPavement,
} from '../../../../../../services/types/Calculator';
import RoadPavementTransportSection from '../Modules/RoadPavementTransportSection';
import EmissionsDevelopmentBuildingConstructionSection from '../Modules/EmissionsDevelopmentBuildingConstructionSection';
import LandUseChangeSectionTransport from '../Modules/LandUseChangeSectionTransport';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

export interface TeamMember {
  imgPath?: string;
  name: string;
  initials: string;
}

interface Props {
  landUseChange: LandUseChange;
  roadPavementTransport: RoadPavement;
  emissionsDevelopmentBuildingConstruction: BuildingConstruction;
  setLandUseChange: React.Dispatch<React.SetStateAction<LandUseChange>>;
  setRoadPavementTransport: React.Dispatch<React.SetStateAction<RoadPavement>>;
  setEmissionsDevelopmentBuildingConstruction: React.Dispatch<
    React.SetStateAction<BuildingConstruction>
  >;
}

const ConstructionTab: React.FC<Props> = ({
  landUseChange,
  roadPavementTransport,
  emissionsDevelopmentBuildingConstruction,
  setLandUseChange,
  setRoadPavementTransport,
  setEmissionsDevelopmentBuildingConstruction,
}) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const icon = `${process.env.PUBLIC_URL}/img/GPS.svg`;

  const handleRowToggle = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((rowIndex) => rowIndex !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const chevron = (index: number) =>
    expandedRows.includes(index) ? 'chevron-down' : 'chevron-right';

  return (
    <div className="timeline-list">
      {landUseChange && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(0)}
                text={landUseChange.name}
                type="button"
                onClick={() => handleRowToggle(0)}
              />
              {expandedRows.includes(0) && (
                <>
                  <span id={`tr-landUse-${landUseChange.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-landUse-${landUseChange.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.LandUseChange')}
                  </UncontrolledTooltip>

                  <span id={`tr-landUselink-${landUseChange.moduleId}`}>
                    <a
                      title="LandUseChangeInfo"
                      aria-label={t('moduleInstructions.LandUseChange')}
                      href={landUseChange.infoUrl ? landUseChange.infoUrl : ''}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={icon} alt="" className="calculator-icon-svg" />
                    </a>
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-landUselink-${landUseChange.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    Click Here to view IPCC Map to determine Land Cover Type if Unknown
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(0) && landUseChange.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={landUseChange.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          <span className="deleted-toolkit" />
          {expandedRows.includes(0) && (
            <LandUseChangeSectionTransport
              landUseChange={landUseChange}
              onUpdateLandUseChange={(updatedLandUseChange) =>
                setLandUseChange(updatedLandUseChange)
              }
            />
          )}
        </div>
      )}

      {roadPavementTransport && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(1)}
                text={roadPavementTransport.name}
                type="button"
                onClick={() => handleRowToggle(1)}
              />
              {expandedRows.includes(1) && (
                <>
                  <span id={`tr-roadPavement-${roadPavementTransport.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-roadPavement-${roadPavementTransport.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.RoadPavement')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(1) && roadPavementTransport.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={roadPavementTransport.subTotal} /> t
                CO<sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(1) && (
            <RoadPavementTransportSection
              roadPavementTransport={roadPavementTransport}
              onUpdateRoadPavementTransport={(updatedRoadPavementTransport) =>
                setRoadPavementTransport(updatedRoadPavementTransport)
              }
            />
          )}
        </div>
      )}

      {emissionsDevelopmentBuildingConstruction && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(2)}
                text={emissionsDevelopmentBuildingConstruction.name}
                type="button"
                onClick={() => handleRowToggle(2)}
              />
            </div>
            {!expandedRows.includes(2) &&
              emissionsDevelopmentBuildingConstruction.subTotal != null && (
                <div className="timeline-row-subtotal-calculator">
                  Subtotal:{' '}
                  <NumberFormatterSinSeparador
                    value={emissionsDevelopmentBuildingConstruction.subTotal}
                  />{' '}
                  t CO<sub>2</sub>
                </div>
              )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(2) && (
            <EmissionsDevelopmentBuildingConstructionSection
              emissionsDevelopmentBuildingConstruction={emissionsDevelopmentBuildingConstruction}
              onUpdateEmissionsDevelopmentBuildingConstruction={(
                updatedEmissionsDevelopmentBuildingConstruction
              ) =>
                setEmissionsDevelopmentBuildingConstruction(
                  updatedEmissionsDevelopmentBuildingConstruction
                )
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ConstructionTab;
